import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { LanguageProvider } from '@/context/LanguageContext';
import { AuthProvider } from '@/hooks/useAuth';
import { Toaster } from '@/components/ui/sonner';
import MainRoutes from '@/routes/MainRoutes';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import { Route, Routes } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ActivateProfile from '@/pages/ActivateProfile';
import CreateAdminUser from '@/pages/CreateAdminUser';
import CityDataSync from '@/pages/dev/CityDataSync';
import AutomaticSitemapIntegration from '@/components/seo/AutomaticSitemapIntegration';
import GlobalSEOManager from '@/components/seo/GlobalSEOManager';
import LanguageRedirectManager from '@/components/routing/LanguageRedirectManager';
import { ABTestingProvider } from '@/components/analytics/ABTestingProvider';
import { ConversionTracking } from '@/components/analytics/ConversionTracking';
import { HeatmapTracking } from '@/components/analytics/HeatmapTracking';
import { FunnelAnalytics } from '@/components/analytics/FunnelAnalytics';
import TranslationJobAlerts from '@/components/translation/TranslationJobAlerts';
import './App.css';
import { setupGlobalErrorHandlers } from '@/utils/errorLogger';

const MassiveCityTranslationTool = lazy(() => import('@/pages/dev/MassiveCityTranslationTool'));
const TranslationProgressDashboard = lazy(() => import('@/pages/dev/TranslationProgressDashboard'));
const TranslationValidationTool = lazy(() => import('@/pages/dev/TranslationValidationTool'));
const TranslationJobMonitor = lazy(() => import('@/pages/dev/TranslationJobMonitor'));
const TranslationTestingTool = lazy(() => import('@/pages/dev/TranslationTestingTool'));
const TranslationOrchestrator = lazy(() => import('@/pages/dev/TranslationOrchestrator'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: (failureCount, error) => {
        if (error instanceof Error && error.message.includes('404')) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

function App() {
  if (typeof window !== 'undefined') {
    setupGlobalErrorHandlers();
  }
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <LanguageProvider>
              <AuthProvider>
                <ABTestingProvider>
                  <GlobalSEOManager />
                  <AutomaticSitemapIntegration />
                  <LanguageRedirectManager />
                  <ConversionTracking />
                  <HeatmapTracking />
                  <FunnelAnalytics />
                  <TranslationJobAlerts />
                  <Routes>
                    <Route path="/activate-profile/:token" element={<ActivateProfile />} />
                    <Route path="/create-admin" element={<CreateAdminUser />} />
                    <Route path="/dev/city-sync" element={<CityDataSync />} />
                    <Route path="/dev/massive-translation" element={
                      <Suspense fallback={<div className="p-6">Loading...</div>}>
                        <MassiveCityTranslationTool />
                      </Suspense>
                    } />
                    <Route path="/dev/translation-dashboard" element={
                      <Suspense fallback={<div className="p-6">Loading...</div>}>
                        <TranslationProgressDashboard />
                      </Suspense>
                    } />
                    <Route path="/dev/translation-validation" element={
                      <Suspense fallback={<div className="p-6">Loading...</div>}>
                        <TranslationValidationTool />
                      </Suspense>
                    } />
                    <Route path="/dev/translation-monitor" element={
                      <Suspense fallback={<div className="p-6">Loading...</div>}>
                        <TranslationJobMonitor />
                      </Suspense>
                    } />
                    <Route path="/dev/translation-testing" element={
                      <Suspense fallback={<div className="p-6">Loading...</div>}>
                        <TranslationTestingTool />
                      </Suspense>
                    } />
                    <Route path="/dev/translation-orchestrator" element={
                      <Suspense fallback={<div className="p-6">Loading...</div>}>
                        <TranslationOrchestrator />
                      </Suspense>
                    } />
                    <Route path="*" element={<MainRoutes />} />
                  </Routes>
                  <Toaster 
                    position="top-right"
                    richColors
                    closeButton
                    duration={4000}
                  />
                </ABTestingProvider>
              </AuthProvider>
            </LanguageProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
